(function attachAgoFlashSalesClientGuard(global, factory) {
  'use strict';

  var api = factory();
  global.AgoFlashSalesClientGuardV1 = api;
  if (global.document) api.boot(global);
}(typeof window !== 'undefined' ? window : globalThis, function createAgoFlashSalesClientGuard() {
  'use strict';

  var PREVIEW_THEME_ID = '183648190751';
  var CONFIG_ELEMENT_ID = 'AgoFlashSalesClientGuardV1Config';
  var PRODUCT_CACHE = {};
  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '[data-checkout-button]',
    'a[href^="/checkout"]',
    'a[href*="/checkout?"]',
    '.btn-checkout',
    '.checkout-button',
    '.go-to-checkout',
  ].join(',');

  function assert(condition, message) {
    if (!condition) throw new Error(message);
  }

  function normalizeId(value) {
    var source = String(value == null ? '' : value).trim();
    var match = source.match(/\/(\d+)$/);
    return match ? match[1] : source.replace(/^\D+/, '');
  }

  function normalizeToken(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function positiveInteger(value, fallback) {
    var number = Number.parseInt(String(value), 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function normalizePolicy(source) {
    if (!source || typeof source !== 'object') return null;
    var matchTags = Array.isArray(source.matchTags)
      ? source.matchTags.map(normalizeToken).filter(Boolean)
      : [];
    var policy = {
      key: normalizeToken(source.key),
      enabled: source.enabled !== false,
      matchTags: matchTags,
      maxQuantity: positiveInteger(source.maxQuantity, 1),
      message: String(source.message || '').trim(),
      aggregateByProduct: source.aggregateByProduct !== false,
    };
    if (policy.key !== 'flash-sales' || !policy.enabled || !policy.matchTags.length || !policy.message) return null;
    return policy;
  }

  function activePolicy(config) {
    var shared = Array.isArray(config && config.sharedPolicies) ? config.sharedPolicies : [];
    var source = shared.find(function findFlashSalesPolicy(policy) {
      return normalizeToken(policy && policy.key) === 'flash-sales';
    }) || config && config.fallbackPolicy;
    return normalizePolicy(source);
  }

  function validateConfig(config) {
    assert(config && typeof config === 'object', 'Flash Sales limit configuration is missing.');
    assert(config.schemaVersion === 1, 'Unsupported Flash Sales limit configuration.');
    assert(config.environment === 'PREVIEW_ONLY', 'Flash Sales client guard must remain Preview-only.');
    assert(normalizeId(config.targetThemeId) === PREVIEW_THEME_ID, 'Unexpected target theme.');
    assert(normalizeId(config.runtimeThemeId) === PREVIEW_THEME_ID, 'Flash Sales client guard is not running on the approved Preview Theme.');
    assert(activePolicy(config), 'No active Flash Sales policy is available.');
    return config;
  }

  function parseConfig(documentRef) {
    var element = documentRef.getElementById(CONFIG_ELEMENT_ID);
    assert(element, 'Flash Sales limit configuration element is missing.');
    return validateConfig(JSON.parse(element.textContent || '{}'));
  }

  function cartItems(cart) {
    return Array.isArray(cart && cart.items) ? cart.items : [];
  }

  function itemHandle(item) {
    if (item && item.handle) return normalizeToken(item.handle);
    var url = String(item && item.url || '');
    var match = url.match(/\/products\/([^/?#]+)/i);
    return match ? normalizeToken(decodeURIComponent(match[1])) : '';
  }

  function itemProductId(item) {
    return normalizeId(item && (item.product_id || item.productId || item.id));
  }

  function productHasPolicyTag(product, policy) {
    var tags = Array.isArray(product && product.tags) ? product.tags.map(normalizeToken) : [];
    return policy.matchTags.some(function hasTag(tag) { return tags.indexOf(tag) !== -1; });
  }

  function fetchProduct(host, handle) {
    var normalizedHandle = normalizeToken(handle);
    if (!normalizedHandle) return Promise.resolve(null);
    if (PRODUCT_CACHE[normalizedHandle]) return PRODUCT_CACHE[normalizedHandle];
    PRODUCT_CACHE[normalizedHandle] = host.fetch('/products/' + encodeURIComponent(normalizedHandle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function parse(response) {
      if (!response.ok) return null;
      return response.json();
    }).catch(function unavailable() { return null; });
    return PRODUCT_CACHE[normalizedHandle];
  }

  function evaluateEligibleCartItems(items, policy, eligibilityByProductId) {
    var groups = {};
    items.forEach(function inspect(item) {
      var productId = itemProductId(item);
      if (!productId || !eligibilityByProductId[productId]) return;
      if (!groups[productId]) {
        groups[productId] = { productId: productId, quantity: 0, items: [] };
      }
      groups[productId].quantity += positiveInteger(item.quantity, 0);
      groups[productId].items.push(item);
    });
    return Object.keys(groups).map(function mapGroup(key) { return groups[key]; }).filter(function overLimit(group) {
      return group.quantity > policy.maxQuantity;
    });
  }

  function resolveViolations(host, cart, policy) {
    var items = cartItems(cart);
    return Promise.all(items.map(function readProduct(item) {
      var productId = itemProductId(item);
      return fetchProduct(host, itemHandle(item)).then(function resolve(product) {
        return { productId: productId, matches: productHasPolicyTag(product, policy) };
      });
    })).then(function evaluated(resolutions) {
      var eligibilityByProductId = {};
      resolutions.forEach(function remember(resolution) {
        if (resolution.productId && resolution.matches) eligibilityByProductId[resolution.productId] = true;
      });
      return evaluateEligibleCartItems(items, policy, eligibilityByProductId);
    });
  }

  function cartRoot(host) {
    var root = String(host.Shopify && host.Shopify.routes && host.Shopify.routes.root || '/');
    if (root.charAt(0) !== '/') root = '/' + root;
    return root.replace(/\/{2,}/g, '/').replace(/([^/])$/, '$1/');
  }

  function fetchCart(host) {
    return host.fetch(cartRoot(host) + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function parse(response) {
      if (!response.ok) throw new Error('Unable to read the Shopify cart.');
      return response.json();
    });
  }

  function replaceCartFragment(documentRef, selector, html) {
    documentRef.querySelectorAll(selector).forEach(function replace(element) {
      element.innerHTML = html;
    });
  }

  function refreshCartPresentation(host) {
    var isCartPage = host.document.body.classList.contains('template-cart');
    var view = isCartPage ? 'pagejs' : 'js';
    var url = cartRoot(host) + 'cart?view=' + view + '&_ago_flash=' + Date.now();
    return host.fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html', 'Cache-Control': 'no-cache' },
    }).then(function parse(response) {
      if (!response.ok) throw new Error('Unable to refresh the Shopify cart presentation.');
      return response.text();
    }).then(function render(markup) {
      var payload = markup.split('[split_t4nt0]')[1];
      var fragments = payload && payload.split('[split_t4nt]');
      if (!fragments || fragments.length < 5) throw new Error('The Shopify cart presentation was incomplete.');

      var itemCount = String(fragments[0] || '0').trim();
      Array.from(host.document.body.classList).forEach(function removeOldCount(className) {
        if (className.indexOf('t4_cart_count_') === 0) host.document.body.classList.remove(className);
      });
      host.document.body.classList.add('t4_cart_count_' + itemCount);
      host.document.querySelectorAll('.jsccount').forEach(function updateCount(element) {
        element.textContent = itemCount;
      });
      replaceCartFragment(host.document, '.js_cat_items', fragments[2]);
      replaceCartFragment(host.document, '.js_cat_dics', fragments[3]);
      replaceCartFragment(host.document, '.js_cat_ttprice', fragments[4]);

      if (host.geckoShopify && typeof host.geckoShopify.WidgetCartUpdateMobile === 'function') {
        host.geckoShopify.WidgetCartUpdateMobile();
      }
      if (host.jQuery_T4NT) host.jQuery_T4NT('body').trigger('refresh_currency');
      host.document.dispatchEvent(new host.CustomEvent('ago:cart-fragment-repaired'));
      return fragments;
    });
  }

  function itemContainers(documentRef, item) {
    var key = String(item && item.key || '');
    if (!key) return [];
    var selector = '[data-id="' + CSS.escape(key) + '"], #miniupdates_' + CSS.escape(key);
    var found = [];
    documentRef.querySelectorAll(selector).forEach(function match(input) {
      var container = input.closest('.mini_cart_item, .cart_item, [data-cart-item]');
      if (container && found.indexOf(container) === -1) found.push(container);
    });
    return found;
  }

  function clearNotices(documentRef) {
    documentRef.querySelectorAll('[data-ago-flash-sales-limit-notice]').forEach(function remove(notice) { notice.remove(); });
  }

  function mountNotices(host) {
    clearNotices(host.document);
  }

  function setCheckoutState(host, violations, config) {
    var blocked = violations.length > 0;
    host.document.documentElement.classList.toggle('ago-flash-sales-checkout-is-blocked', blocked);
    host.document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function update(control) {
      control.classList.toggle('ago-flash-sales-client-guard__checkout', blocked);
      if (blocked) control.setAttribute('aria-describedby', 'AgoFlashSalesLimitCheckoutMessage');
      else control.removeAttribute('aria-describedby');
    });
    var message = host.document.getElementById('AgoFlashSalesLimitCheckoutMessage');
    if (!blocked) {
      if (message) message.remove();
      return;
    }
    if (!message) {
      message = host.document.createElement('div');
      message.id = 'AgoFlashSalesLimitCheckoutMessage';
      message.className = 'ago-flash-sales-client-guard__checkout-message';
      message.setAttribute('role', 'alert');
      host.document.body.appendChild(message);
    }
    message.textContent = String(config.copy && config.copy.checkoutBlocked || 'Reduce each Flash Sales product to 1 item before checkout.');
  }

  function repairGroup(host, group, config) {
    var root = cartRoot(host);
    var remaining = activePolicy(config).maxQuantity;
    var updates = group.items.map(function calculate(item) {
      var quantity = Math.min(positiveInteger(item.quantity, 0), remaining);
      remaining -= quantity;
      return { key: item.key, quantity: quantity };
    });
    return updates.reduce(function chain(request, update) {
      return request.then(function updateLine() {
        return host.fetch(root + 'cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: update.key, quantity: update.quantity }),
        }).then(function ensure(response) {
          if (!response.ok) throw new Error('Unable to update the Flash Sales cart line.');
        });
      });
    }, Promise.resolve());
  }

  function readViolations(host, config) {
    return fetchCart(host).then(function validate(cart) {
      return resolveViolations(host, cart, activePolicy(config));
    });
  }

  function validateCart(host, config) {
    if (host.__AgoFlashSalesClientGuardRepairPromiseV1) {
      return host.__AgoFlashSalesClientGuardRepairPromiseV1;
    }

    var cycle = readViolations(host, config).then(function reconcile(violations) {
      host.__AgoFlashSalesClientGuardViolationsV1 = violations;
      mountNotices(host);
      if (!violations.length) {
        setCheckoutState(host, [], config);
        return [];
      }

      return violations.reduce(function repairEach(sequence, group) {
        return sequence.then(function repairCurrentGroup() {
          return repairGroup(host, group, config);
        });
      }, Promise.resolve()).then(function refreshAndVerify() {
        return refreshCartPresentation(host).then(function verifyRefreshedCart() {
          return readViolations(host, config);
        });
      }).then(function finish(finalViolations) {
        host.__AgoFlashSalesClientGuardViolationsV1 = finalViolations;
        mountNotices(host);
        setCheckoutState(host, finalViolations, config);
        return finalViolations;
      });
    });

    host.__AgoFlashSalesClientGuardRepairPromiseV1 = cycle.finally(function releaseRepair() {
      host.__AgoFlashSalesClientGuardRepairPromiseV1 = null;
    });
    return host.__AgoFlashSalesClientGuardRepairPromiseV1;
  }

  function targetIsCheckout(target) {
    return Boolean(target && target.closest && target.closest(CHECKOUT_SELECTOR));
  }

  function blockCheckoutWhenInvalid(host, config, event) {
    var violations = host.__AgoFlashSalesClientGuardViolationsV1 || [];
    if (!violations.length) return false;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    setCheckoutState(host, violations, config);
    var message = host.document.getElementById('AgoFlashSalesLimitCheckoutMessage');
    if (message && typeof message.focus === 'function') {
      message.tabIndex = -1;
      message.focus({ preventScroll: true });
    }
    return true;
  }

  function isCartAddForm(form) {
    var action = String(form && form.getAttribute('action') || '');
    return /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(action);
  }

  function selectedVariantId(form) {
    var input = form && form.querySelector('[name="id"]');
    return normalizeId(input && input.value);
  }

  function scheduleValidator(host, config, delay) {
    host.clearTimeout(host.__AgoFlashSalesClientGuardTimerV1);
    host.__AgoFlashSalesClientGuardTimerV1 = host.setTimeout(function run() {
      validateCart(host, config).catch(function log(error) {
        host.console.warn('[AGO Flash Sales limit]', error.message || error);
      });
    }, delay == null ? 350 : delay);
  }

  function boot(host) {
    var config;
    try {
      config = parseConfig(host.document);
    } catch (error) {
      host.console.error('[AGO Flash Sales limit]', error);
      return;
    }
    host.document.addEventListener('click', function captureClick(event) {
      if (targetIsCheckout(event.target)) blockCheckoutWhenInvalid(host, config, event);
      if (event.target && event.target.closest('.plus, .minus, .js_addtc, [name="add"], .cart_ac_remove')) {
        scheduleValidator(host, config, 650);
      }
    }, true);
    host.document.addEventListener('submit', function captureSubmit(event) {
      if (isCartAddForm(event.target) && selectedVariantId(event.target)) scheduleValidator(host, config, 650);
      if (targetIsCheckout(event.target)) blockCheckoutWhenInvalid(host, config, event);
    }, true);
    ['cart:refresh', 'cart:updated', 'ajaxProduct:added', 'ago:cart-fragment-repaired'].forEach(function listen(eventName) {
      host.document.addEventListener(eventName, function afterCartChange() { scheduleValidator(host, config, 80); });
    });
    var mutationScheduled = false;
    new host.MutationObserver(function observe() {
      if (mutationScheduled) return;
      mutationScheduled = true;
      host.setTimeout(function afterMutation() {
        mutationScheduled = false;
        if (host.document.querySelector('.mini_cart_item, .cart_item, [data-cart-item]')) scheduleValidator(host, config, 80);
      }, 80);
    }).observe(host.document.documentElement, { childList: true, subtree: true });
    host.addEventListener('pageshow', function afterPageShow() { scheduleValidator(host, config, 0); });
    scheduleValidator(host, config, 0);
  }

  return {
    activePolicy: activePolicy,
    boot: boot,
    evaluateEligibleCartItems: evaluateEligibleCartItems,
    itemHandle: itemHandle,
    itemProductId: itemProductId,
    normalizePolicy: normalizePolicy,
    normalizeToken: normalizeToken,
    productHasPolicyTag: productHasPolicyTag,
    validateConfig: validateConfig,
  };
}));
